import { useContext } from 'react'
import { useSelector } from 'react-redux'
import { getApolloContext } from '@apollo/client'
import { selectAuth } from '../../../services/auth/selectors'
import { useDivinationFeatureStateQuery } from '../queries.generated'
import { useMidnightRefresh } from './useMidnightRefresh'

export interface UseTarotFeatureStateParams {
  chatSessionId?: string | null
  skip?: boolean
}

export interface UseTarotFeatureStateResult {
  isCurrentMonth: boolean
  isInBadQuestionState: boolean
  badQuestionOriginal: string | null
  badQuestionRephrasedQuestions: string[]
  loading: boolean
  monthlyChatSessionId: string | null
  refetch: () => void
  evictAndRefetch: () => void
}

/**
 * Fetches tarot-specific feature state and determines whether the given
 * chatSessionId matches the current month's tarot session. Provides
 * cache-evicting refetch for scenarios where the backend state has changed
 * (e.g., after a card pull).
 */
export const useTarotFeatureState = ({
  chatSessionId,
  skip = false,
}: UseTarotFeatureStateParams): UseTarotFeatureStateResult => {
  const auth = useSelector(selectAuth)
  const apolloContext = useContext(getApolloContext())
  const client = apolloContext?.client

  const { data, loading, refetch } = useDivinationFeatureStateQuery({
    skip: skip || !chatSessionId,
    variables: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      tarotChatSessionId: chatSessionId,
    },
    context: {
      jwt: auth.get('jwt'),
    },
    fetchPolicy: 'cache-and-network',
  })

  useMidnightRefresh(refetch, skip || !chatSessionId)

  const tarotState = data?.divinationFeatureState?.tarotState
  const monthlyChatSessionId = tarotState?.chatSessionId

  const isCurrentMonth = !chatSessionId
    || !monthlyChatSessionId
    || chatSessionId === monthlyChatSessionId

  const evictAndRefetch = () => {
    if (client) {
      client.cache.evict({ fieldName: 'divinationFeatureState' })
      client.cache.gc()
    }
    refetch()
  }

  return {
    isCurrentMonth,
    isInBadQuestionState: tarotState?.isInBadQuestionState ?? false,
    badQuestionOriginal: tarotState?.badQuestionOriginal ?? null,
    badQuestionRephrasedQuestions: tarotState?.badQuestionRephrasedQuestions ?? [],
    loading,
    monthlyChatSessionId: monthlyChatSessionId ?? null,
    refetch,
    evictAndRefetch,
  }
}
